import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./Sidebar.css";
import { SignedIn, useClerk, useUser } from "@clerk/clerk-react";
import { PanelLeft, Search, Database, Newspaper, GraduationCap, MoreHorizontal, Trash2, Info, Shield } from "lucide-react";
import ThemeToggle from "./ThemeToggle";

/**
 * Sidebar component – navigation, conversation history, explore links, user account.
 *
 * Props (8 logical groups, some are setter pairs):
 *  1. sidebarOpen / setSidebarOpen
 *  2. handleNewChat
 *  3. conversations
 *  4. searchQuery / setSearchQuery
 *  5. currentConversationId / loadConversation
 *  6. hasSubscription
 *  7. handleSubscribe / isCheckoutLoading
 *  8. handleManageSubscription / isPortalLoading
 *
 * NOTE: 12 individual props total. If more state is added, consider
 * grouping billing props into a Context or a single `billing` object prop.
 */
export default function Sidebar(props) {
    if (props.isEmbedMode) {
        return <SidebarView {...props} user={null} clerk={null} />;
    }
    return <SidebarWithClerk {...props} />;
}

function SidebarWithClerk(props) {
    const clerk = useClerk();
    const { user } = useUser();
    return <SidebarView {...props} user={user} clerk={clerk} />;
}

function SidebarView({
    sidebarOpen,
    setSidebarOpen,
    handleNewChat,
    conversations,
    searchQuery,
    setSearchQuery,
    currentConversationId,
    loadConversation,
    hasSubscription,
    isSuperUser,
    handleSubscribe,
    isCheckoutLoading,
    handleManageSubscription,
    isPortalLoading,
    deleteConversation,
    onOpenDisclaimer,
    isEmbedMode = false,
    user,
    clerk,
}) {
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const deleteModalRef = useRef(null);
    const previousFocusRef = useRef(null);

    const closeDeleteModal = useCallback(() => setDeleteTargetId(null), []);

    useEffect(() => {
        if (!deleteTargetId) return;
        previousFocusRef.current = document.activeElement;
        const dialog = deleteModalRef.current;
        if (dialog) {
            const firstBtn = dialog.querySelector("button");
            if (firstBtn) firstBtn.focus();
        }
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                closeDeleteModal();
                return;
            }
            if (e.key !== "Tab" || !dialog) return;
            const focusable = dialog.querySelectorAll('button:not([disabled])');
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) { e.preventDefault(); last.focus(); }
            } else {
                if (document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            const previousFocus = previousFocusRef.current;
            if (previousFocus && document.contains(previousFocus) && typeof previousFocus.focus === "function") {
                previousFocus.focus();
            }
        };
    }, [deleteTargetId, closeDeleteModal]);

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.conversation-menu-container')) {
                setOpenMenuId(null);
            }
            if (!e.target.closest('.account-menu-wrapper')) {
                setShowAccountMenu(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <>
            {deleteTargetId && (
                <div className="delete-modal-overlay" onClick={closeDeleteModal}>
                    <div
                        ref={deleteModalRef}
                        className="delete-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="delete-modal-title"
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 id="delete-modal-title">Delete chat</h3>
                        <p>Are you sure you want to delete this chat?</p>
                        <div className="delete-modal-actions">
                            <button className="cancel-btn" onClick={closeDeleteModal}>Cancel</button>
                            <button className="delete-confirm-btn" onClick={() => {
                                deleteConversation(deleteTargetId);
                                closeDeleteModal();
                            }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar Backdrop */}
            <div
                className={`sidebar-backdrop ${sidebarOpen ? 'open' : ''}`}
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
            />
            {/* Sidebar */}
            <nav aria-label="Main navigation" className={`sidebar ${sidebarOpen ? 'open' : 'closed'}${isEmbedMode ? ' sidebar-embed' : ''}`}>
                <div className="sidebar-top">
                    <div className="sidebar-header">
                        <div className="sidebar-branding" aria-hidden={!sidebarOpen}>
                            <img src={`${process.env.PUBLIC_URL}/revelo.png`} alt="Revelo" className="sidebar-revelo-logo" />
                        </div>
                    </div>
                    <div className="sidebar-actions">
                        <button
                            className="new-chat-btn"
                            aria-label="New chat"
                            onClick={() => {
                                if (window.innerWidth <= 768) {
                                    setSidebarOpen(false);
                                } else if (!sidebarOpen) {
                                    setSidebarOpen(true);
                                }
                                handleNewChat();
                            }}
                        >
                            <div className="icon-circle">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                            </div>
                            <span className="new-chat-text">New chat</span>
                        </button>

                        {!isEmbedMode && (
                        <div
                            className="search-container"
                            onClick={() => {
                                if (!sidebarOpen) {
                                    setSidebarOpen(true);
                                    setTimeout(() => {
                                        const searchInput = document.querySelector('.search-input');
                                        if (searchInput) searchInput.focus();
                                    }, 100);
                                }
                            }}
                        >
                            <div className="search-icon-wrapper">
                                <Search size={16} aria-hidden="true" />
                            </div>
                            <label htmlFor="sidebar-search" className="sr-only">Search past conversations</label>
                            <input
                                id="sidebar-search"
                                type="search"
                                placeholder="Search"
                                aria-label="Search past conversations"
                                autoComplete="off"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        )}
                    </div>

                    {!isEmbedMode && conversations.filter(c => (c.title || "New Conversation").toLowerCase().includes(searchQuery.toLowerCase())).length > 0 && (
                        <div className="sidebar-history mt-2">
                            <small className="sidebar-recent-label">RECENT</small>
                            {conversations.filter(c => (c.title || "New Conversation").toLowerCase().includes(searchQuery.toLowerCase())).map(conv => (
                                <div
                                    key={conv._id}
                                    className={`history-item-container ${currentConversationId === conv._id ? 'active' : ''}`}
                                >
                                    <button
                                        className={`history-item ${currentConversationId === conv._id ? 'active' : ''}`}
                                        onClick={() => {
                                            if (window.innerWidth <= 768) {
                                                setSidebarOpen(false);
                                            }
                                            loadConversation(conv._id);
                                        }}
                                        title={conv.title || "New Conversation"}
                                    >
                                        <span className="history-item-title">{conv.title || "New Conversation"}</span>
                                    </button>
                                    <div className="conversation-menu-container">
                                        <button
                                            className="conversation-menu-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenMenuId(openMenuId === conv._id ? null : conv._id);
                                            }}
                                            aria-label="Conversation options"
                                            aria-haspopup="true"
                                            aria-expanded={openMenuId === conv._id}
                                        >
                                            <MoreHorizontal size={16} aria-hidden="true" focusable="false" />
                                        </button>

                                        {openMenuId === conv._id && (
                                            <div className="conversation-dropdown-menu">
                                                <button
                                                    className="dropdown-item delete-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteTargetId(conv._id);
                                                        setOpenMenuId(null);
                                                    }}
                                                >
                                                    <Trash2 size={14} aria-hidden="true" />
                                                    <span>Delete</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Explore Section */}
                <div className="sidebar-explore mt-2">
                    <small className="sidebar-recent-label">EXPLORE</small>
                    <Link
                        to="/sources"
                        className="history-item explore-item"
                        onClick={() => { if (window.innerWidth <= 768) setSidebarOpen(false); }}
                    >
                        <Database size={16} className="explore-icon" aria-hidden="true" />
                        <span>Data Sources</span>
                    </Link>
                    <Link
                        to="/training"
                        className="history-item explore-item"
                        title="Training Resources"
                        onClick={() => { if (window.innerWidth <= 768) setSidebarOpen(false); }}
                    >
                        <GraduationCap size={16} className="explore-icon" aria-hidden="true" />
                        <span>Training Resources</span>
                    </Link>
                    <Link
                        to="/news"
                        className="history-item explore-item"
                        onClick={() => { if (window.innerWidth <= 768) setSidebarOpen(false); }}
                    >
                        <Newspaper size={16} className="explore-icon" aria-hidden="true" />
                        <span>News</span>
                    </Link>
                    {isSuperUser && (
                        <Link
                            to="/admin"
                            className="history-item explore-item"
                            onClick={() => { if (window.innerWidth <= 768) setSidebarOpen(false); }}
                        >
                            <Shield size={16} className="explore-icon" aria-hidden="true" />
                            <span>Admin</span>
                        </Link>
                    )}

                </div>

                {/* Sidebar Upgrade Section — hidden for super users (company employees) */}
                {!isEmbedMode && (
                <SignedIn>
                    {!isSuperUser && (
                        <div className="sidebar-upgrade-wrapper">
                            {!hasSubscription ? (
                                <button
                                    className="sidebar-upgrade-btn"
                                    onClick={handleSubscribe}
                                    disabled={isCheckoutLoading}
                                >
                                    {isCheckoutLoading ? "Loading..." : "Upgrade to Pro"}
                                </button>
                            ) : (
                                <button
                                    className="sidebar-upgrade-btn manage-sub-btn"
                                    onClick={handleManageSubscription}
                                    disabled={isPortalLoading}
                                >
                                    {isPortalLoading ? "Loading..." : "Manage Subscription"}
                                </button>
                            )}
                        </div>
                    )}
                </SignedIn>
                )}

                {/* Sidebar Bottom - User Account Section */}
                <div className="sidebar-bottom">
                    <div className="sidebar-controls-row">
                        <ThemeToggle />
                        <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title={sidebarOpen ? "Close sidebar" : "Open sidebar"} aria-label="Toggle sidebar" aria-expanded={sidebarOpen}>
                            <PanelLeft size={18} aria-hidden="true" />
                        </button>
                    </div>
                    {!isEmbedMode && (
                    <SignedIn>
                        <div className="account-menu-wrapper">
                            <button
                                type="button"
                                className="user-profile-row"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAccountMenu((open) => !open);
                                }}
                                aria-label="Account menu"
                                aria-haspopup="true"
                                aria-expanded={showAccountMenu}
                                aria-controls="account-menu"
                            >
                                <img src={user?.imageUrl} alt="" className="user-avatar-sidebar" />
                                <div className="user-info-sidebar">
                                    <div className="user-name-sidebar">{user?.fullName || "User"}</div>
                                    <div className="user-plan-sidebar">
                                        {hasSubscription ? "Pro Plan" : (user?.primaryEmailAddress?.emailAddress || "Free Plan")}
                                    </div>
                                </div>
                                <div className="user-settings-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
                                </div>
                            </button>

                            {showAccountMenu && (
                                <div id="account-menu" className="account-menu" role="menu" aria-label="Account actions">
                                    <button type="button" role="menuitem" className="account-menu-item" onClick={(e) => { e.stopPropagation(); clerk.openUserProfile(); setShowAccountMenu(false); }}>Account Settings</button>
                                    {!isSuperUser && (
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="account-menu-item"
                                            disabled={hasSubscription ? isPortalLoading : isCheckoutLoading}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (hasSubscription) {
                                                    handleManageSubscription();
                                                } else {
                                                    handleSubscribe();
                                                }
                                                setShowAccountMenu(false);
                                            }}
                                        >
                                            {hasSubscription
                                                ? (isPortalLoading ? "Loading..." : "Manage Subscription")
                                                : (isCheckoutLoading ? "Loading..." : "Upgrade to Pro")}
                                        </button>
                                    )}
                                    {onOpenDisclaimer && (
                                        <button type="button" role="menuitem" className="account-menu-item" onClick={(e) => { e.stopPropagation(); onOpenDisclaimer(); setShowAccountMenu(false); }}>Disclaimer</button>
                                    )}
                                    <button type="button" role="menuitem" className="account-menu-item" onClick={(e) => { e.stopPropagation(); clerk.signOut(); setShowAccountMenu(false); }}>Sign Out</button>
                                </div>
                            )}
                        </div>
                    </SignedIn>
                    )}
                    {isEmbedMode && onOpenDisclaimer && (
                        <button
                            type="button"
                            className="history-item explore-item sidebar-embed-disclaimer"
                            onClick={onOpenDisclaimer}
                            title="Disclaimer"
                            aria-label="Disclaimer"
                        >
                            <Info size={16} className="explore-icon" aria-hidden="true" />
                            <span>Disclaimer</span>
                        </button>
                    )}
                </div>
            </nav>
        </>
    );
}
